import React, { useEffect } from 'react';
import xss from 'xss';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/nextjs';

import storage from 'legacy/common/utils/storage';

import {
  incrementEventViews,
  incrementPromoterCodeViews,
  incrementTrackingLinkViews,
  publicGetEvent
} from 'legacy/events/service';

import EventInvite from 'legacy/components/pages/redirects/EventInvite/EventInvite';

import EventPageScreen from 'legacy/events/screens/EventPage.screen';
import AnalyticsPage from 'legacy/events/screens/AnalyticsPage.screen';
import DiscountCodesPage from 'legacy/events/screens/DiscountCodesPage.screen';
import CreateEvent from 'legacy/events/screens/CreateEvent.screen';
import UploadMoments from 'legacy/events/screens/UploadMoments.screen';
import ViewEventProvider, {
  useViewEvent
} from 'features/events/context/ViewEvent';

const trackUniqueVisitor = (
  storageKey: string,
  trackingValue: string,
  trackingCall: () => Promise<void>
) => {
  const trackedItems = storage.getItem(storageKey);
  try {
    if (trackedItems) {
      const parsedTrackedItems = JSON.parse(trackedItems);
      if (parsedTrackedItems.includes(trackingValue)) {
        return;
      }

      storage.setItem(
        storageKey,
        JSON.stringify([...parsedTrackedItems, trackingValue])
      );
    } else {
      storage.setItem(storageKey, JSON.stringify([trackingValue]));
    }

    trackingCall();
  } catch (error) {
    Sentry.captureMessage(
      `Error while attempting to track ${storageKey}: ${error.message}`,
      { level: 'warning' }
    );
  }
};

export const EventPage = ({ router, seoInfo }): JSX.Element => {
  const { event } = useViewEvent();
  const eventData = event || seoInfo;
  const { query } = router;
  const { view, invite, t: trackingId, p: promoterCode } = query;

  const user = useSelector((state: any) => state.user?.user);
  const eventId = query.slug;

  useEffect(() => {
    if (promoterCode) {
      trackUniqueVisitor('trackedPromoterCodeViews', promoterCode, () =>
        incrementPromoterCodeViews(eventId, promoterCode)
      );
    }
  }, [promoterCode]);

  useEffect(() => {
    trackUniqueVisitor('trackedViews', eventId, () =>
      incrementEventViews(eventId)
    );
    if (trackingId) {
      trackUniqueVisitor('trackedLinkViews', trackingId, () =>
        incrementTrackingLinkViews(eventId, trackingId)
      );
    }
  }, []);

  if (invite && user) {
    return (
      <div className="container">
        <EventInvite />
      </div>
    );
  }

  if (view === 'analytics') {
    return <AnalyticsPage />;
  }

  if (view === 'discountCodes') {
    return <DiscountCodesPage />;
  }

  if (view === 'edit') {
    return <CreateEvent eventId={eventId} />;
  }

  if (view === 'upload-moments') {
    return <UploadMoments eventId={eventId} />;
  }

  return (
    <>
      {eventData && (
        <Head>
          <title>{`${eventData.title} | Bounce`}</title>
          <meta property="og:type" content="website" />
          <meta property="fb:app_id" content="1855802768063770" />

          <meta
            property="og:url"
            content={`https://bouncelife.com/events/${eventData._id}`}
          />

          <meta property="og:title" content={eventData.title} />

          {eventData.photo?.url && (
            <meta property="og:image" content={eventData.photo.url} />
          )}

          <meta property="og:description" content={eventData.description} />
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: xss(
                JSON.stringify({
                  '@context': 'http://schema.org',
                  '@type': 'Event',
                  name: `${eventData?.title}`,
                  startDate: `${eventData?.startDate}`,
                  endDate: `${eventData?.endDate}`,
                  image: `https://bouncelife.com/events/${eventData?._id}`,
                  location: {
                    '@type': 'Place',
                    address: `${eventData?.address}`,
                    description: `${eventData?.description}`,
                    organizer: `${eventData?.host?.name}`
                  }
                })
              )
            }}
          />
        </Head>
      )}

      <EventPageScreen />
    </>
  );
};

EventPage.getInitialProps = async ({ req, query }) => {
  if (req) {
    try {
      const data = await publicGetEvent(query.slug);

      return {
        seoInfo: data
      };
    } catch (err) {
      // TODO: if an error occurs here, we dont show it
      return {};
    }
  }

  return {};
};

EventPage.getLayout = (page) => <ViewEventProvider>{page}</ViewEventProvider>;

export default EventPage;
